/**
 * Determines if a navigation item should be considered active based on the current path
 * @param itemPath - The path of the navigation item (e.g., "/blog")
 * @param currentPath - The current location pathname (e.g., "/blog/1")
 * @param options - Configuration options for the active state logic
 * @returns true if the item should be considered active
 */
export const isNavigationItemActive = (
  itemPath: string,
  currentPath: string,
  options: {
    /** Whether to consider child routes as active (e.g., /blog/1 makes /blog active) */
    includeChildRoutes?: boolean
    /** Custom matcher function for special cases */
    customMatcher?: (itemPath: string, currentPath: string) => boolean
  } = {},
): boolean => {
  const { includeChildRoutes = false, customMatcher } = options;

  // Remove query parameters for comparison
  const baseItemPath = itemPath.split("?")[0];

  // Use custom matcher if provided
  if (customMatcher) {
    return customMatcher(baseItemPath, currentPath);
  }

  // Exact match
  if (currentPath === baseItemPath) {
    return true;
  }

  // Child route match (if enabled)
  if (includeChildRoutes && currentPath.startsWith(`${baseItemPath}/`)) {
    return true;
  }

  return false;
};

/**
 * Routes that should be active when on child pages
 */
const ROUTES_WITH_CHILD_PAGES = ["/blog", "/fichiers", "/bons", "/calendrier"];

/**
 * Helper function to check if a route should include child routes
 */
export const shouldIncludeChildRoutes = (itemPath: string): boolean => {
  return ROUTES_WITH_CHILD_PAGES.includes(itemPath);
};
